import {
  AGENT_ROLES,
  CLAUDE_MODELS,
  KnockOutRuleSchema,
  LimitRuleTypeSchema,
  resolveAgentModel,
  type AgentRole,
  type KnockOutRule,
  type LimitRule,
  type LimitRuleParams,
  type LlmEffort,
  DEMO_ENTITY,
} from '@paguz/contracts';
import {
  Badge,
  Button,
  cn,
  inputBaseClass,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@paguz/ui';
import { Trash2Icon, Volume2Icon } from 'lucide-react';
import {
  Checkbox,
  Field,
  Select,
  Switch,
  compactInputClass,
} from '../../components/form-controls.tsx';
import { KNOCK_OUT } from '../../lib/labels.ts';
import { AGENT_LABEL, CONVERSATIONAL_AGENTS } from '../agents/labels.ts';
import { UsagePanel } from '../agents/usage-panel.tsx';
import { FormCard, IntField, MoneyField, RateField, SettingSection } from './setting-editor.tsx';

/** Secciones P1 de configuración (límites M8, originación, IA y voz), editables en la Oleada 2. */

const RULE_LABEL: Readonly<Record<string, string>> = {
  MIN_PER_TX: 'Mínimo por compra',
  MAX_PER_TX: 'Máximo por compra',
  MAX_DAILY_AMOUNT: 'Máximo diario',
  MAX_MONTHLY_AMOUNT: 'Máximo mensual',
  MAX_DAILY_COUNT: 'Compras por día',
  VELOCITY: 'Velocidad',
  NEW_PAYEE_THRESHOLD: 'Reto para beneficiario nuevo',
  BLOCKED_MCC: 'MCC bloqueados',
  BLOCKED_ALIAS: 'Llaves bloqueadas',
};

function defaultParams(type: LimitRuleParams['ruleType']): LimitRuleParams {
  const amount = { amount: '1000000.00', currency: 'COP' as const };
  switch (type) {
    case 'MAX_DAILY_COUNT':
      return { ruleType: type, count: 10 };
    case 'VELOCITY':
      return { ruleType: type, count: 3, windowMinutes: 10 };
    case 'BLOCKED_MCC':
      return { ruleType: type, mccs: [] };
    case 'BLOCKED_ALIAS':
      return { ruleType: type, aliases: [] };
    case 'MIN_PER_TX':
    case 'MAX_PER_TX':
    case 'MAX_DAILY_AMOUNT':
    case 'MAX_MONTHLY_AMOUNT':
    case 'NEW_PAYEE_THRESHOLD':
      return { ruleType: type, amount };
  }
}

export function LimitsSection() {
  return (
    <SettingSection
      settingKey="limits"
      title="Límites transaccionales"
      description="Reglas M8 que evalúa el orquestador antes de autorizar: resultado ALLOW, CHALLENGE (SCA adicional) o DENY con código PZ-LIM. Aplican a compras nuevas."
      labels={{}}
    >
      {({ draft, set, error, canEdit }) => {
        const setRules = (rules: LimitRule[]) => {
          set(['rules'], rules);
        };
        const missing = LimitRuleTypeSchema.options.filter(
          (t) => !draft.rules.some((r) => r.params.ruleType === t),
        );
        return (
          <FormCard
            title="Reglas"
            actions={
              canEdit &&
              missing.length > 0 && (
                <Select
                  aria-label="Agregar regla"
                  value=""
                  className="w-56"
                  onChange={(e) => {
                    const type = e.target.value as LimitRuleParams['ruleType'];
                    setRules([
                      ...draft.rules,
                      {
                        id: `lim_new_${draft.rules.length + 1}`,
                        scope: 'TENANT',
                        scopeRef: null,
                        action: 'DENY',
                        active: true,
                        params: defaultParams(type),
                      },
                    ]);
                  }}
                >
                  <option value="">+ Agregar regla…</option>
                  {missing.map((t) => (
                    <option key={t} value={t}>
                      {RULE_LABEL[t]}
                    </option>
                  ))}
                </Select>
              )
            }
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Regla</TableHead>
                  <TableHead className="w-64">Parámetro</TableHead>
                  <TableHead>Acción</TableHead>
                  <TableHead>Activa</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {draft.rules.map((rule, i) => {
                  const p = rule.params;
                  const path = ['rules', i, 'params'] as const;
                  return (
                    <TableRow key={rule.id}>
                      <TableCell className="font-semibold">
                        {RULE_LABEL[p.ruleType] ?? p.ruleType}
                        <div className="text-xs font-normal text-muted-foreground">
                          {rule.scope === 'TENANT'
                            ? 'Toda la entidad'
                            : `${rule.scope} ${rule.scopeRef ?? ''}`}
                        </div>
                      </TableCell>
                      <TableCell>
                        {'amount' in p ? (
                          <MoneyField
                            label="Monto"
                            value={p.amount}
                            onChange={(v) => {
                              set([...path, 'amount'], v);
                            }}
                            error={error([...path, 'amount'])}
                          />
                        ) : 'windowMinutes' in p ? (
                          <div className="grid grid-cols-2 gap-2">
                            <IntField
                              label="Compras"
                              suffix="#"
                              value={p.count}
                              onChange={(v) => {
                                set([...path, 'count'], v);
                              }}
                              error={error([...path, 'count'])}
                            />
                            <IntField
                              label="Ventana"
                              suffix="min"
                              value={p.windowMinutes}
                              onChange={(v) => {
                                set([...path, 'windowMinutes'], v);
                              }}
                              error={error([...path, 'windowMinutes'])}
                            />
                          </div>
                        ) : 'count' in p ? (
                          <IntField
                            label="Compras al día"
                            suffix="#"
                            value={p.count}
                            onChange={(v) => {
                              set([...path, 'count'], v);
                            }}
                            error={error([...path, 'count'])}
                          />
                        ) : 'mccs' in p ? (
                          <Field
                            label="MCC (separados por coma)"
                            htmlFor={`mcc-${i}`}
                            error={error([...path, 'mccs', 0])}
                          >
                            <input
                              id={`mcc-${i}`}
                              className={cn(inputBaseClass, compactInputClass)}
                              value={p.mccs.join(', ')}
                              onChange={(e) => {
                                set(
                                  [...path, 'mccs'],
                                  e.target.value
                                    .split(',')
                                    .map((x) => x.trim())
                                    .filter(Boolean),
                                );
                              }}
                            />
                          </Field>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            {p.aliases.length} llaves (se editan por API)
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          aria-label={`Acción de ${RULE_LABEL[p.ruleType] ?? p.ruleType}`}
                          value={rule.action}
                          onChange={(e) => {
                            set(['rules', i, 'action'], e.target.value);
                          }}
                        >
                          <option value="DENY">Rechaza</option>
                          <option value="CHALLENGE">Pide SCA</option>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Switch
                          aria-label={`Regla ${RULE_LABEL[p.ruleType] ?? p.ruleType} activa`}
                          checked={rule.active}
                          onCheckedChange={(v) => {
                            set(['rules', i, 'active'], v);
                          }}
                        />
                        {error(['rules', i, 'active']) && (
                          <p className="text-xs text-destructive">
                            {error(['rules', i, 'active'])}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        {canEdit && (
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label="Quitar regla"
                            onClick={() => {
                              setRules(draft.rules.filter((_, j) => j !== i));
                            }}
                          >
                            <Trash2Icon />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </FormCard>
        );
      }}
    </SettingSection>
  );
}

const ORIGINATION_LABELS: Readonly<Record<string, string>> = {
  minBusinessAgeMonths: 'Antigüedad mínima del negocio',
  'age.min': 'Edad mínima',
  'age.max': 'Edad máxima',
  'coverage.otherCityAction': 'Fuera de cobertura',
  paymentCapacityFactor: 'Factor prudencial de capacidad',
  maxSalesTriangulationDivergence: 'Divergencia máxima de ventas',
  maxDebtToSurplusRatio: 'Endeudamiento máximo',
  reapplyAfterDays: 'Reintentar después de',
  knockOuts: 'Knock-outs',
};

export function OriginationSection() {
  return (
    <SettingSection
      settingKey="originationPolicy"
      title="Política de originación"
      description="Elegibilidad, documentos, capacidad de pago y scorecard que usa el motor de decisión (código determinístico)."
      labels={ORIGINATION_LABELS}
    >
      {({ draft, set, error }) => (
        <>
          <FormCard title="Elegibilidad">
            <div className="grid gap-5 md:grid-cols-3">
              <IntField
                label="Antigüedad mínima del negocio"
                suffix="meses"
                value={draft.minBusinessAgeMonths}
                onChange={(v) => {
                  set(['minBusinessAgeMonths'], v);
                }}
                error={error(['minBusinessAgeMonths'])}
              />
              <IntField
                label="Edad mínima"
                suffix="años"
                value={draft.age.min}
                onChange={(v) => {
                  set(['age', 'min'], v);
                }}
                error={error(['age', 'min'])}
              />
              <IntField
                label="Edad máxima"
                suffix="años"
                value={draft.age.max}
                onChange={(v) => {
                  set(['age', 'max'], v);
                }}
                error={error(['age', 'max'])}
              />
              <Field
                label="Ciudad fuera de cobertura"
                htmlFor="orig-city"
                hint={`${draft.coverage.municipalities.length} municipios cubiertos`}
              >
                <Select
                  id="orig-city"
                  value={draft.coverage.otherCityAction}
                  onChange={(e) => {
                    set(['coverage', 'otherCityAction'], e.target.value);
                  }}
                >
                  <option value="HUMAN_REVIEW">Revisión humana</option>
                  <option value="REJECT">Rechazo</option>
                </Select>
              </Field>
              <IntField
                label="Reintentar después de"
                suffix="días"
                value={draft.reapplyAfterDays}
                onChange={(v) => {
                  set(['reapplyAfterDays'], v);
                }}
                error={error(['reapplyAfterDays'])}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Documentos obligatorios: {draft.requiredDocuments.all.join(', ')} + al menos{' '}
              {draft.requiredDocuments.anyOf.min} soporte de ventas.
            </p>
          </FormCard>
          <FormCard title="Capacidad de pago">
            <div className="grid gap-5 md:grid-cols-3">
              <RateField
                label="Factor prudencial de capacidad"
                value={draft.paymentCapacityFactor}
                onChange={(v) => {
                  set(['paymentCapacityFactor'], v);
                }}
                error={error(['paymentCapacityFactor'])}
                hint="Cuota máxima = factor × excedente"
              />
              <RateField
                label="Divergencia máxima de ventas"
                value={draft.maxSalesTriangulationDivergence}
                onChange={(v) => {
                  set(['maxSalesTriangulationDivergence'], v);
                }}
                error={error(['maxSalesTriangulationDivergence'])}
                hint="Por encima, revisión humana"
              />
              <RateField
                label="Endeudamiento máximo"
                value={draft.maxDebtToSurplusRatio}
                onChange={(v) => {
                  set(['maxDebtToSurplusRatio'], v);
                }}
                error={error(['maxDebtToSurplusRatio'])}
              />
            </div>
          </FormCard>
          <FormCard title="Knock-outs" description="Condiciones que descartan la solicitud.">
            <div className="grid gap-2 md:grid-cols-2">
              {KnockOutRuleSchema.options.map((rule: KnockOutRule) => (
                <Checkbox
                  key={rule}
                  label={KNOCK_OUT[rule]}
                  checked={draft.knockOuts.includes(rule)}
                  onCheckedChange={(on) => {
                    set(
                      ['knockOuts'],
                      on ? [...draft.knockOuts, rule] : draft.knockOuts.filter((k) => k !== rule),
                    );
                  }}
                />
              ))}
            </div>
            {error(['knockOuts']) && (
              <p className="text-sm text-destructive">{error(['knockOuts'])}</p>
            )}
          </FormCard>
          <FormCard
            title="Scorecard"
            description="Pesos de cada factor (deben sumar 100 %); los rangos se editan por API."
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Factor</TableHead>
                  <TableHead className="w-44">Peso</TableHead>
                  <TableHead>Rangos → puntos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {draft.scorecard.map((f, i) => (
                  <TableRow key={f.code}>
                    <TableCell className="font-semibold">{f.label}</TableCell>
                    <TableCell>
                      <RateField
                        label={`Peso de ${f.label}`}
                        value={f.weight}
                        onChange={(v) => {
                          set(['scorecard', i, 'weight'], v);
                        }}
                        error={error(['scorecard', i, 'weight'])}
                      />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {f.bands.map((b) => `[${b.min}, ${b.max ?? '∞'}) → ${b.points}`).join(' · ')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </FormCard>
        </>
      )}
    </SettingSection>
  );
}

const BUDGET_SCOPE = {
  TURN: 'Por turno',
  SESSION: 'Por sesión',
  CUSTOMER_DAY: 'Por cliente al día',
  TENANT_DAY: 'Entidad al día',
} as const;
const BUDGET_ACTION = { DEGRADE: 'Degradar modelo', PAUSE: 'Pausar', NOTIFY: 'Avisar' } as const;
const MODELS = Object.values(CLAUDE_MODELS);
const EFFORTS: readonly LlmEffort[] = ['low', 'medium', 'high', 'xhigh', 'max'];
const VOICES = [
  ['es-MX-JorgeMultilingualNeural', 'Jorge (latino neutro, masculino)'],
  ['es-MX-DaliaMultilingualNeural', 'Dalia (latina neutra, femenina)'],
  ['es-US-PalomaNeural', 'Paloma (latina, femenina)'],
  ['es-CO-SalomeNeural', 'Salomé (Colombia, femenina)'],
  ['es-CO-GonzaloNeural', 'Gonzalo (Colombia, masculina)'],
] as const;

/** Muestra de voz local (navegador); la voz real la sintetiza `SpeechPort` en el servidor. */
function playSample(voice: string) {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  const utterance = new SpeechSynthesisUtterance(
    `Hola, soy tu asesor de ${DEMO_ENTITY.name}. ¿En qué te ayudo hoy?`,
  );
  utterance.lang = 'es-CO';
  const match = window.speechSynthesis
    .getVoices()
    .find(
      (v) =>
        v.lang.startsWith('es') &&
        (voice.includes('Gonzalo') ? /male|jorge|diego|gonzalo/i.test(v.name) : true),
    );
  if (match) utterance.voice = match;
  window.speechSynthesis.speak(utterance);
}

export function AiSection() {
  return (
    <SettingSection
      settingKey="ai"
      title="IA y voz"
      description="Modelo y esfuerzo por rol de agente, presupuestos y voz. Cambiar el modelo de un rol exige pasar sus evaluaciones. La IA prepara y explica; nunca mueve dinero ni aprueba créditos."
      labels={{}}
      aside={() => <UsagePanel />}
    >
      {({ draft, set, error }) => (
        <>
          <FormCard
            title="Modelos por rol"
            description="Los roles sin cambio usan el modelo por defecto del contrato."
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rol</TableHead>
                  <TableHead>Modelo</TableHead>
                  <TableHead>Esfuerzo</TableHead>
                  <TableHead className="text-right">Máx. tokens</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {AGENT_ROLES.map((role: AgentRole) => {
                  const cfg = resolveAgentModel(role, draft);
                  const custom = draft.models[role] !== undefined;
                  return (
                    <TableRow key={role}>
                      <TableCell>
                        {AGENT_LABEL[role]}
                        {custom && (
                          <Badge variant="highlight" className="ml-2">
                            Ajustado
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          aria-label={`Modelo de ${AGENT_LABEL[role]}`}
                          value={cfg.model}
                          onChange={(e) => {
                            set(['models', role], { ...cfg, model: e.target.value });
                          }}
                        >
                          {MODELS.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          aria-label={`Esfuerzo de ${AGENT_LABEL[role]}`}
                          value={cfg.effort}
                          onChange={(e) => {
                            set(['models', role], { ...cfg, effort: e.target.value });
                          }}
                        >
                          {EFFORTS.map((x) => (
                            <option key={x} value={x}>
                              {x}
                            </option>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cfg.maxTokens.toLocaleString('es-CO')}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </FormCard>
          <FormCard
            title="Presupuestos"
            description="El tope global diario lo fija LLM_DAILY_BUDGET_USD en el servidor (ver consumo del día)."
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Alcance</TableHead>
                  <TableHead className="w-48">Límite USD</TableHead>
                  <TableHead className="w-48">Límite tokens</TableHead>
                  <TableHead>Al superar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {draft.budgets.map((b, i) => (
                  <TableRow key={b.scope}>
                    <TableCell>{BUDGET_SCOPE[b.scope]}</TableCell>
                    <TableCell>
                      <Field
                        label="USD"
                        htmlFor={`bud-usd-${i}`}
                        error={error(['budgets', i, 'limitUsd'])}
                      >
                        <input
                          id={`bud-usd-${i}`}
                          className={cn(inputBaseClass, compactInputClass)}
                          inputMode="decimal"
                          value={b.limitUsd ?? ''}
                          placeholder="—"
                          onChange={(e) => {
                            set(['budgets', i, 'limitUsd'], e.target.value.trim() || null);
                          }}
                        />
                      </Field>
                    </TableCell>
                    <TableCell>
                      <Field label="Tokens" htmlFor={`bud-tok-${i}`}>
                        <input
                          id={`bud-tok-${i}`}
                          className={cn(inputBaseClass, compactInputClass)}
                          inputMode="numeric"
                          value={b.limitTokens ?? ''}
                          placeholder="—"
                          onChange={(e) => {
                            const d = e.target.value.replace(/\D/g, '');
                            set(['budgets', i, 'limitTokens'], d ? Number.parseInt(d, 10) : null);
                          }}
                        />
                      </Field>
                    </TableCell>
                    <TableCell>
                      <Select
                        aria-label={`Acción del presupuesto ${BUDGET_SCOPE[b.scope]}`}
                        value={b.action}
                        onChange={(e) => {
                          set(['budgets', i, 'action'], e.target.value);
                        }}
                      >
                        {(Object.keys(BUDGET_ACTION) as (keyof typeof BUDGET_ACTION)[]).map((a) => (
                          <option key={a} value={a}>
                            {BUDGET_ACTION[a]}
                          </option>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </FormCard>
          <FormCard
            title="Agentes habilitados"
            description="Equivale al kill switch global (Agentes › Kill switch)."
          >
            {CONVERSATIONAL_AGENTS.map((agent) => (
              <Switch
                key={agent}
                label={AGENT_LABEL[agent]}
                checked={draft.enabled[agent]}
                onCheckedChange={(v) => {
                  set(['enabled', agent], v);
                }}
              />
            ))}
          </FormCard>
          <FormCard title="Voz">
            <Switch
              label="Voz habilitada"
              description="El cliente puede hablarle al asesor y escuchar las respuestas."
              checked={draft.voice.enabled}
              onCheckedChange={(v) => {
                set(['voice', 'enabled'], v);
              }}
            />
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Voz" htmlFor="voice-name" className="w-72">
                <Select
                  id="voice-name"
                  value={draft.voice.voice}
                  onChange={(e) => {
                    set(['voice', 'voice'], e.target.value);
                  }}
                >
                  {VOICES.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                  {!VOICES.some(([v]) => v === draft.voice.voice) && (
                    <option value={draft.voice.voice}>{draft.voice.voice}</option>
                  )}
                </Select>
              </Field>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  playSample(draft.voice.voice);
                }}
              >
                <Volume2Icon /> Escuchar muestra
              </Button>
            </div>
          </FormCard>
        </>
      )}
    </SettingSection>
  );
}
